import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  searchProjets,
  getProjets,
  getProjetById,
  createProjet,
  updateProjet,
  deleteProjet,
  toggleProjetActive,
} from "../../../../services/projetsServices";

import { PROJETS_INITIAL_FORM } from "../data/projetsAdminData";

import {
  buildProjetsSearchParams,
  getProjetsStats,
  normalizeProjetForm,
  validateProjetForm,
} from "../utils/projetsAdminUtils";

export default function useProjetsAdmin() {
  const mountedRef = useRef(true);

  const [q, setQ] = useState("");
  const [stateFilter, setStateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(24);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionId, setActionId] = useState(null);

  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const [formOpen, setFormOpen] = useState(false);
  const [currentId, setCurrentId] = useState(null);
  const [form, setForm] = useState(PROJETS_INITIAL_FORM);
  const [fieldErrors, setFieldErrors] = useState({});

  const [previewItem, setPreviewItem] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  const totalPages = useMemo(() => {
    return Math.max(Math.ceil(total / limit), 1);
  }, [total, limit]);

  const stats = useMemo(() => {
    return getProjetsStats(items, total);
  }, [items, total]);

  const showNotice = useCallback((message) => {
    setNotice(message);

    window.clearTimeout(showNotice.timeout);
    showNotice.timeout = window.setTimeout(() => {
      setNotice("");
    }, 3500);
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params = buildProjetsSearchParams({
        q,
        stateFilter,
        page,
        limit,
      });

      const res = await searchProjets(params);

      const hasNoAdminResult =
        !res?.items?.length && !q && stateFilter === "all" && page === 1;

      if (hasNoAdminResult) {
        const pub = await getProjets();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        return;
      }

      if (!mountedRef.current) return;

      setItems(Array.isArray(res?.items) ? res.items : []);
      setTotal(Number(res?.total || 0));
    } catch (fetchError) {
      console.error("Erreur chargement projets :", fetchError);

      try {
        const pub = await getProjets();
        const fallbackItems = Array.isArray(pub) ? pub : [];

        if (!mountedRef.current) return;

        setItems(fallbackItems.slice(0, limit));
        setTotal(fallbackItems.length);
        setError(
          "Le chargement administrateur a échoué. Affichage d’une liste de secours.",
        );
      } catch (fallbackError) {
        console.error("Erreur fallback projets :", fallbackError);

        if (!mountedRef.current) return;

        setItems([]);
        setTotal(0);
        setError("Impossible de charger les projets pour le moment.");
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [q, stateFilter, page, limit]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onSubmitSearch = useCallback(
    (event) => {
      event?.preventDefault?.();

      if (page !== 1) {
        setPage(1);
      } else {
        fetchData();
      }
    },
    [fetchData, page],
  );

  const changeStateFilter = useCallback((value) => {
    setStateFilter(value);
    setPage(1);
  }, []);

  const changeLimit = useCallback((value) => {
    setLimit(Number(value) || 24);
    setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setQ("");
    setStateFilter("all");
    setLimit(24);
    setPage(1);
  }, []);

  const openCreate = useCallback(() => {
    setCurrentId(null);
    setForm(PROJETS_INITIAL_FORM);
    setFieldErrors({});
    setFormOpen(true);
  }, []);

  const openEdit = useCallback(async (id) => {
    if (!id) return;

    try {
      setSaving(true);
      setError("");

      const row = await getProjetById(id);

      if (!row) {
        setError("Projet introuvable.");
        return;
      }

      setCurrentId(id);
      setForm({
        titre: row.titre || "",
        description: row.description || "",
        image_projet_url: row.image_projet_url || "",
        page_detail_url: row.page_detail_url || "",
        is_active: !!row.is_active,
      });
      setFieldErrors({});
      setFormOpen(true);
    } catch (editError) {
      console.error("Erreur ouverture projet :", editError);
      setError("Impossible d’ouvrir ce projet.");
    } finally {
      setSaving(false);
    }
  }, []);

  const openEditFromPreview = useCallback(async () => {
    if (!previewItem?.id) return;

    const id = previewItem.id;
    setPreviewItem(null);
    await openEdit(id);
  }, [openEdit, previewItem]);

  const closeForm = useCallback(() => {
    setFormOpen(false);
    setCurrentId(null);
    setForm(PROJETS_INITIAL_FORM);
    setFieldErrors({});
    setMediaPickerOpen(false);
  }, []);

  const updateFormField = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));

    setFieldErrors((previous) => {
      if (!previous[field]) return previous;

      const next = { ...previous };
      delete next[field];
      return next;
    });
  }, []);

  const onSave = useCallback(async () => {
    const errors = validateProjetForm(form);

    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError("");

      const payload = normalizeProjetForm(form);

      if (currentId) {
        await updateProjet(currentId, payload);
        showNotice("Projet modifié avec succès.");
      } else {
        await createProjet(payload);
        showNotice("Projet créé avec succès.");
      }

      closeForm();
      await fetchData();
    } catch (saveError) {
      console.error("Erreur enregistrement projet :", saveError);
      setError("Impossible d’enregistrer ce projet.");
    } finally {
      setSaving(false);
    }
  }, [closeForm, currentId, fetchData, form, showNotice]);

  const onToggle = useCallback(
    async (id) => {
      if (!id) return;

      try {
        setActionId(`toggle-${id}`);
        setError("");

        await toggleProjetActive(id);
        showNotice("Statut de publication mis à jour.");
        await fetchData();
      } catch (toggleError) {
        console.error("Erreur changement statut projet :", toggleError);
        setError("Impossible de modifier le statut de ce projet.");
      } finally {
        setActionId(null);
      }
    },
    [fetchData, showNotice],
  );

  const openPreview = useCallback(
    async (id) => {
      if (!id) return;

      const localItem = items.find((item) => item.id === id);

      if (localItem) {
        setPreviewItem(localItem);
        return;
      }

      try {
        setActionId(`preview-${id}`);

        const row = await getProjetById(id);

        if (row) {
          setPreviewItem(row);
        }
      } catch (previewError) {
        console.error("Erreur aperçu projet :", previewError);
        setError("Impossible d’afficher l’aperçu de ce projet.");
      } finally {
        setActionId(null);
      }
    },
    [items],
  );

  const closePreview = useCallback(() => {
    setPreviewItem(null);
  }, []);

  const openDelete = useCallback(
    (id) => {
      const localItem = items.find((item) => item.id === id);

      setDeleteItem(
        localItem || {
          id,
          titre: "Ce projet",
        },
      );
    },
    [items],
  );

  const closeDelete = useCallback(() => {
    setDeleteItem(null);
  }, []);

  const onDelete = useCallback(async () => {
    if (!deleteItem?.id) return;

    try {
      setDeleting(true);
      setError("");

      await deleteProjet(deleteItem.id);

      setDeleteItem(null);
      showNotice("Projet supprimé.");
      await fetchData();
    } catch (deleteError) {
      console.error("Erreur suppression projet :", deleteError);
      setError("Impossible de supprimer ce projet.");
    } finally {
      setDeleting(false);
    }
  }, [deleteItem, fetchData, showNotice]);

  const openMediaPicker = useCallback(() => {
    setMediaPickerOpen(true);
  }, []);

  const closeMediaPicker = useCallback(() => {
    setMediaPickerOpen(false);
  }, []);

  const handleSelectMedia = useCallback(
    (media) => {
      if (!media) return;

      updateFormField("image_projet_url", media.url || "");
      setMediaPickerOpen(false);
    },
    [updateFormField],
  );

  return {
    q,
    setQ,
    stateFilter,
    page,
    setPage,
    limit,
    items,
    total,
    totalPages,
    stats,

    loading,
    saving,
    deleting,
    actionId,
    error,
    notice,

    formOpen,
    currentId,
    form,
    fieldErrors,
    previewItem,
    deleteItem,
    mediaPickerOpen,

    fetchData,
    onSubmitSearch,
    changeStateFilter,
    changeLimit,
    resetFilters,

    openCreate,
    openEdit,
    openEditFromPreview,
    closeForm,
    updateFormField,
    onSave,

    onToggle,

    openPreview,
    closePreview,

    openDelete,
    closeDelete,
    onDelete,

    openMediaPicker,
    closeMediaPicker,
    handleSelectMedia,
  };
}